import {
  type ButtonHTMLAttributes,
  type FC,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import {createPortal} from 'react-dom'
import classNames from 'classnames'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import type {PlacesType} from 'react-tooltip'

import {ArrowIcon} from 'components/Basic/SvgIcon/Native'
import {TooltipWrapper} from 'components/Basic/TooltipWrapper'
import {KEY_CODES} from 'definitions/constant'
import {DATA_RESIDENCY_FOCUS_STATE} from 'definitions/tenantConfig'
import {SettingsRoutesEnum} from 'interfaces'
import {PERMISSIONS} from 'rbac/config'
import {usePermission} from 'rbac/withPermission'
import './NoStorageRouteTooltip.css'

/** Lets the pointer cross the gap between a trigger and the popover. */
const HOVER_CLOSE_DELAY_MS = 100
const TRIGGER_ATTRIBUTE = 'data-no-storage-route'

export type NoStorageRouteTriggerProps = Pick<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'aria-controls'
> &
  Required<
    Pick<
      ButtonHTMLAttributes<HTMLButtonElement>,
      | 'aria-disabled'
      | 'aria-expanded'
      | 'onClick'
      | 'onKeyDown'
      | 'onMouseEnter'
      | 'onMouseLeave'
    >
  > & {[TRIGGER_ATTRIBUTE]: string}

export type GetNoStorageRouteTriggerProps = (
  routeName: string,
) => NoStorageRouteTriggerProps

interface NoStorageRouteTooltipProps {
  place: PlacesType
  /** Renders the navigation; closed routes spread `getTriggerProps(name)`. */
  children: (getTriggerProps: GetNoStorageRouteTriggerProps) => ReactNode
}

const isForwardTab = (event: KeyboardEvent) =>
  event.key === KEY_CODES.TAB && !event.shiftKey

/**
 * @description One popover shared by every Analytics view that is closed for
 * a No Storage tenant. Hover previews it; a click (or Enter / Space) pins it
 * until the user clicks outside, clicks the same trigger again, moves focus
 * away or presses Escape. Moving to another closed view glides the same
 * popover there instead of closing and reopening it.
 *
 * Users who can manage billing get a link to Subscription to enable storage;
 * everyone else is told to ask an administrator.
 *
 * The popover is portalled to `document.body`: the route pills sit inside a
 * scroll container and a transformed, clipped wrapper, which would otherwise
 * flip the popover to the side and stop it receiving clicks.
 */
export const NoStorageRouteTooltip: FC<NoStorageRouteTooltipProps> = ({
  place,
  children,
}) => {
  const {t} = useTranslation(['analytics'])
  const canEnableStorage = usePermission([PERMISSIONS.BILLING_DETAILS.WRITE])
  const idBase = useId()
  const tooltipId = `no-storage-route-tooltip${idBase}`
  const dialogId = `${tooltipId}-dialog`
  const titleId = `${tooltipId}-title`

  const triggerContainerRef = useRef<HTMLSpanElement>(null)
  const popoverRef = useRef<HTMLDialogElement>(null)
  const linkRef = useRef<HTMLAnchorElement>(null)
  const hoverCloseTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const [activeRoute, setActiveRoute] = useState<string | null>(null)
  const [isHovered, setIsHovered] = useState(false)
  const [isPinned, setIsPinned] = useState(false)
  const [isGliding, setIsGliding] = useState(false)
  const isOpen = activeRoute !== null && (isHovered || isPinned)

  const getTriggerKey = (routeName: string) => `${tooltipId}:${routeName}`
  const getActiveTrigger = () =>
    activeRoute === null
      ? null
      : triggerContainerRef.current?.querySelector<HTMLElement>(
          `[${TRIGGER_ATTRIBUTE}="${getTriggerKey(activeRoute)}"]`,
        )

  const cancelHoverClose = () => clearTimeout(hoverCloseTimerRef.current)

  const showRoute = (routeName: string) => {
    cancelHoverClose()
    setIsGliding(isOpen && routeName !== activeRoute)
    setActiveRoute(routeName)
  }

  const closeOnHoverEnd = () => {
    cancelHoverClose()
    hoverCloseTimerRef.current = setTimeout(
      () => setIsHovered(false),
      HOVER_CLOSE_DELAY_MS,
    )
  }

  const close = useCallback(() => {
    clearTimeout(hoverCloseTimerRef.current)
    setIsHovered(false)
    setIsPinned(false)
    setIsGliding(false)
  }, [])

  const togglePinned = (routeName: string) => {
    if (isPinned && activeRoute === routeName) {
      close()
      return
    }
    showRoute(routeName)
    setIsPinned(true)
  }

  // The portalled popover is not next to its triggers in the tab order, so
  // move focus into it and back by hand.
  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!isOpen || event.key !== KEY_CODES.TAB) {
      return
    }
    if (
      event.currentTarget === getActiveTrigger() &&
      isForwardTab(event) &&
      linkRef.current
    ) {
      event.preventDefault()
      linkRef.current.focus()
    } else {
      close()
    }
  }

  const handleLinkKeyDown = (event: KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key !== KEY_CODES.TAB) {
      return
    }
    const trigger = getActiveTrigger()
    if (isForwardTab(event)) {
      close()
      // The link is portalled outside the navigation. Continue from its
      // trigger to the next enabled route, rather than the end of the page.
      const routes = Array.from(
        triggerContainerRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href]',
        ) ?? [],
      ).filter(element => element.tabIndex >= 0)
      const triggerIndex = trigger ? routes.indexOf(trigger) : -1
      const nextRoute = triggerIndex >= 0 ? routes[triggerIndex + 1] : undefined
      if (nextRoute) {
        event.preventDefault()
        nextRoute.focus()
      }
    } else {
      event.preventDefault()
      trigger?.focus()
    }
  }

  const getTriggerProps: GetNoStorageRouteTriggerProps = routeName => {
    const isExpanded = isOpen && activeRoute === routeName
    return {
      [TRIGGER_ATTRIBUTE]: getTriggerKey(routeName),
      // The dialog only exists while open, so only point at it then.
      'aria-controls': isExpanded ? dialogId : undefined,
      'aria-disabled': true,
      'aria-expanded': isExpanded,
      onClick: () => togglePinned(routeName),
      onKeyDown: handleTriggerKeyDown,
      onMouseEnter: () => {
        showRoute(routeName)
        setIsHovered(true)
      },
      onMouseLeave: closeOnHoverEnd,
    }
  }

  useEffect(() => () => clearTimeout(hoverCloseTimerRef.current), [])

  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    const isInside = (target: EventTarget | null) =>
      target instanceof Node &&
      [triggerContainerRef.current, popoverRef.current].some(element =>
        element?.contains(target),
      )

    const closeWhenOutside = (event: Event) => {
      if (!isInside(event.target)) {
        close()
      }
    }
    const closeWhenFocusLeaves = (event: FocusEvent) => {
      const target = event.target
      if (
        target instanceof Node &&
        !getActiveTrigger()?.contains(target) &&
        !popoverRef.current?.contains(target)
      ) {
        close()
      }
    }
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== KEY_CODES.ESCAPE) {
        return
      }
      const trigger = getActiveTrigger()
      const hadFocusInside = isInside(document.activeElement)
      close()
      if (hadFocusInside) {
        trigger?.focus()
      }
    }

    document.addEventListener('pointerdown', closeWhenOutside)
    document.addEventListener('focusin', closeWhenFocusLeaves)
    document.addEventListener('keydown', handleKeyDown)
    // A resize can swap the pills for the route dropdown, hiding the trigger.
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('pointerdown', closeWhenOutside)
      document.removeEventListener('focusin', closeWhenFocusLeaves)
      document.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('resize', close)
    }
  }, [close, isOpen, activeRoute])

  return (
    <>
      <span ref={triggerContainerRef} className="contents">
        {children(getTriggerProps)}
      </span>
      {createPortal(
        <TooltipWrapper
          id={tooltipId}
          isOpen={isOpen}
          imperativeModeOnly
          clickable
          anchorSelect={
            activeRoute === null
              ? undefined
              : `[${TRIGGER_ATTRIBUTE}="${getTriggerKey(activeRoute)}"]`
          }
          place={place}
          positionStrategy="fixed"
          role="presentation"
          noArrow
          noPadding
          className={classNames(
            'no-storage-route-tooltip z-popover !rounded-md border-0.5 border-gray-700 !bg-gray-2400 shadow-lg',
            {
              'no-storage-route-tooltip--from-side': place.startsWith('left'),
              'no-storage-route-tooltip--gliding': isGliding,
            },
          )}
        >
          <dialog
            open
            ref={popoverRef}
            id={dialogId}
            aria-labelledby={titleId}
            data-cy="no-storage-route-tooltip"
            className="static m-0 w-64 max-w-none border-0 bg-transparent p-0 text-left text-inherit"
          >
            {/* Keep hover and event propagation on a presentational wrapper.
                Otherwise the route dropdown closes before the link is clicked. */}
            <div
              role="none"
              className="p-4"
              onMouseDown={event => event.stopPropagation()}
              onMouseEnter={cancelHoverClose}
              onMouseLeave={closeOnHoverEnd}
            >
              <p id={titleId} className="abc-regular text-sm text-white">
                {t('NO_STORAGE_ROUTE_TOOLTIP_TITLE', {routeName: activeRoute})}
              </p>
              <p className="mt-1 karla text-13px leading-5 text-gray-1600">
                {t('NO_STORAGE_ROUTE_TOOLTIP_DESCRIPTION')}
              </p>
              {canEnableStorage ? (
                <Link
                  ref={linkRef}
                  to={{
                    pathname: SettingsRoutesEnum.BILLING,
                    state: DATA_RESIDENCY_FOCUS_STATE,
                  }}
                  data-cy="no-storage-route-tooltip-enable-storage"
                  className="group mt-3 inline-flex items-center gap-2 karla-bold text-13px text-dope-cyan-bright"
                  onKeyDown={handleLinkKeyDown}
                >
                  <span className="group-hover:underline">
                    {t('NO_STORAGE_ROUTE_TOOLTIP_ACTION')}
                  </span>
                  <ArrowIcon
                    width={14}
                    height={8}
                    strokeWidth={0.1}
                    strokeColor="text-dope-cyan-bright"
                    classes="transition-transform duration-fast group-hover:translate-x-0.5 motion-reduce:transition-none"
                  />
                </Link>
              ) : (
                <p className="mt-3 karla text-13px leading-5 text-gray-1600">
                  {t('NO_STORAGE_ROUTE_TOOLTIP_ASK_ADMIN')}
                </p>
              )}
            </div>
          </dialog>
        </TooltipWrapper>,
        document.body,
      )}
    </>
  )
}
